/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The scope used to store exception rules for this scope.
 */
export const ExclusionScope = {
  OrgBlockedRead: "org:blocked_read",
  OrgBlockedAdmin: "org:blocked_admin",
  ProjectBlockedRead: "project:blocked_read",
  ProjectBlockedWrite: "project:blocked_write",
  McpBlockedRead: "mcp:blocked_read",
  McpBlockedWrite: "mcp:blocked_write",
  McpBlockedConnect: "mcp:blocked_connect",
  EnvironmentBlockedRead: "environment:blocked_read",
  EnvironmentBlockedWrite: "environment:blocked_write",
  SkillBlockedRead: "skill:blocked_read",
  SkillBlockedWrite: "skill:blocked_write",
  AssistantBlockedRead: "assistant:blocked_read",
  AssistantBlockedWrite: "assistant:blocked_write",
  PluginBlockedWrite: "plugin:blocked_write",
  RiskPolicyBypass: "risk_policy:bypass",
  WorkloadBlockedRead: "workload:blocked_read",
  WorkloadBlockedWrite: "workload:blocked_write",
} as const;
/**
 * The scope used to store exception rules for this scope.
 */
export type ExclusionScope = ClosedEnum<typeof ExclusionScope>;

/**
 * The type of resource this scope applies to.
 */
export const ResourceType = {
  Org: "org",
  Project: "project",
  Mcp: "mcp",
  Environment: "environment",
  Skill: "skill",
  Assistant: "assistant",
  RiskPolicy: "risk_policy",
  Chat: "chat",
  Agent: "agent",
  Workload: "workload",
} as const;
/**
 * The type of resource this scope applies to.
 */
export type ResourceType = ClosedEnum<typeof ResourceType>;

/**
 * Unique scope identifier.
 */
export const Slug = {
  OrgRead: "org:read",
  OrgBlockedRead: "org:blocked_read",
  OrgAdmin: "org:admin",
  OrgBlockedAdmin: "org:blocked_admin",
  ProjectRead: "project:read",
  ProjectBlockedRead: "project:blocked_read",
  ProjectWrite: "project:write",
  ProjectBlockedWrite: "project:blocked_write",
  McpRead: "mcp:read",
  McpBlockedRead: "mcp:blocked_read",
  McpWrite: "mcp:write",
  McpBlockedWrite: "mcp:blocked_write",
  McpConnect: "mcp:connect",
  McpBlockedConnect: "mcp:blocked_connect",
  EnvironmentRead: "environment:read",
  EnvironmentBlockedRead: "environment:blocked_read",
  EnvironmentWrite: "environment:write",
  EnvironmentBlockedWrite: "environment:blocked_write",
  SkillRead: "skill:read",
  SkillBlockedRead: "skill:blocked_read",
  SkillWrite: "skill:write",
  SkillBlockedWrite: "skill:blocked_write",
  AssistantRead: "assistant:read",
  AssistantBlockedRead: "assistant:blocked_read",
  AssistantWrite: "assistant:write",
  AssistantBlockedWrite: "assistant:blocked_write",
  PluginWrite: "plugin:write",
  PluginBlockedWrite: "plugin:blocked_write",
  RiskPolicyEvaluate: "risk_policy:evaluate",
  RiskPolicyBypass: "risk_policy:bypass",
  RiskPolicyBlock: "risk_policy:block",
  ChatRead: "chat:read",
  ChatWrite: "chat:write",
  LogRead: "log:read",
  AgentRead: "agent:read",
  AgentWrite: "agent:write",
  AgentAuthorize: "agent:authorize",
  AgentTransfer: "agent:transfer",
  WorkloadRead: "workload:read",
  WorkloadBlockedRead: "workload:blocked_read",
  WorkloadWrite: "workload:write",
  WorkloadBlockedWrite: "workload:blocked_write",
  OrgDeviceAgentSync: "org:device_agent_sync",
  OrgHooksIngest: "org:hooks_ingest",
} as const;
/**
 * Unique scope identifier.
 */
export type Slug = ClosedEnum<typeof Slug>;

/**
 * Whether this scope is a first-class permission or an internal storage/evaluation scope.
 */
export const ScopeDefinitionVisibility = {
  UserVisible: "user_visible",
  Internal: "internal",
} as const;
/**
 * Whether this scope is a first-class permission or an internal storage/evaluation scope.
 */
export type ScopeDefinitionVisibility = ClosedEnum<
  typeof ScopeDefinitionVisibility
>;

export type ScopeDefinition = {
  /**
   * Whether an agent principal can hold this scope. Roles may carry scopes agents cannot hold; those are ignored for the role's agent members rather than granted.
   */
  agentEligible: boolean;
  /**
   * What this scope protects.
   */
  description: string;
  /**
   * The scope used to store exception rules for this scope.
   */
  exclusionScope?: ExclusionScope | undefined;
  /**
   * The type of resource this scope applies to.
   */
  resourceType: ResourceType;
  /**
   * Unique scope identifier.
   */
  slug: Slug;
  /**
   * Whether this scope is a first-class permission or an internal storage/evaluation scope.
   */
  visibility: ScopeDefinitionVisibility;
};

/** @internal */
export const ExclusionScope$inboundSchema: z.ZodMiniEnum<
  typeof ExclusionScope
> = z.enum(ExclusionScope);

/** @internal */
export const ResourceType$inboundSchema: z.ZodMiniEnum<typeof ResourceType> = z
  .enum(ResourceType);

/** @internal */
export const Slug$inboundSchema: z.ZodMiniEnum<typeof Slug> = z.enum(Slug);

/** @internal */
export const ScopeDefinitionVisibility$inboundSchema: z.ZodMiniEnum<
  typeof ScopeDefinitionVisibility
> = z.enum(ScopeDefinitionVisibility);

/** @internal */
export const ScopeDefinition$inboundSchema: z.ZodMiniType<
  ScopeDefinition,
  unknown
> = z.pipe(
  z.object({
    agent_eligible: z.boolean(),
    description: z.string(),
    exclusion_scope: z.optional(ExclusionScope$inboundSchema),
    resource_type: ResourceType$inboundSchema,
    slug: Slug$inboundSchema,
    visibility: ScopeDefinitionVisibility$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_eligible": "agentEligible",
      "exclusion_scope": "exclusionScope",
      "resource_type": "resourceType",
    });
  }),
);

export function scopeDefinitionFromJSON(
  jsonString: string,
): SafeParseResult<ScopeDefinition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ScopeDefinition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ScopeDefinition' from JSON`,
  );
}
